/* ==========================================================================
   CART DRAWER — REWORKED
   - Keeps original drawer behavior
   - Does not open on /cart
   - Uses plain-text cart bubble to match icon-cart.liquid
   - No drawer note logic
   - Works with shared cart.js loaded before this file
   - Keeps your Free Gift logic
========================================================================== */

(() => {
  if (customElements.get('cart-drawer')) return;

  const CART_ICON_ID = 'cart-icon-bubble';

  function updateCartCountBubble(count) {
    if (typeof window.updateCartCountBubble === 'function') {
      window.updateCartCountBubble(count);
      return;
    }

    const el = document.getElementById(CART_ICON_ID);
    if (!el) return;

    const n = Number(count || 0);
    let bubble = el.querySelector('.cart-count-bubble');

    if (n <= 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.setAttribute('aria-hidden', 'true');
      el.appendChild(bubble);
    }

    bubble.textContent = n < 100 ? String(n) : '99+';
  }

  function isCartPage() {
    return window.location.pathname === routes.cart_url || !!document.querySelector('cart-page');
  }

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusables(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
      const style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      if (el.closest('[hidden], [aria-hidden="true"]')) return false;
      return true;
    });
  }

  function safeFocus(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (_) {
      el.focus();
    }
  }

  function createFocusTrap(getContainerFn) {
    return {
      active: false,
      lastActive: null,
      onKeyDownBound: null,

      onKeyDown(e) {
        if (!this.active) return;
        if (e.key !== 'Tab') return;

        const container = getContainerFn();
        const focusables = getFocusables(container);
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (container && !container.contains(document.activeElement)) {
          e.preventDefault();
          safeFocus(first);
          return;
        }

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          safeFocus(last);
          return;
        }

        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          safeFocus(first);
        }
      },

      init() {
        this.onKeyDownBound = this.onKeyDown.bind(this);
      },

      activate(triggerEl, focusEl) {
        this.lastActive = triggerEl || document.activeElement;
        this.active = true;
        document.addEventListener('keydown', this.onKeyDownBound, true);
        safeFocus(focusEl);
      },

      deactivate() {
        this.active = false;
        document.removeEventListener('keydown', this.onKeyDownBound, true);
        if (this.lastActive && document.contains(this.lastActive)) safeFocus(this.lastActive);
        this.lastActive = null;
      },
    };
  }

  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this._onKeyUp = this._onKeyUp.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onCartIconClick = this._onCartIconClick.bind(this);
      this._onCartIconKeydown = this._onCartIconKeydown.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);

      this._closeTimer = null;
      this._openTimer = null;

      this._locked = false;
      this._prevInlineScrollBehavior = '';
      this._prevHtmlOverflow = '';
      this._prevHtmlTouchAction = '';
      this._prevHtmlOverscroll = '';

      this._focusTrap = createFocusTrap(() => this._focusTrapContainer());
      this._focusTrap.init();

      this.addEventListener('keyup', this._onKeyUp);

      this._bindOverlay();
      this._bindCartIcon();
      this._bindCloseButtons();
    }

    _bindOverlay() {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (!overlay) return;
      overlay.removeEventListener('click', this._onOverlayClick);
      overlay.addEventListener('click', this._onOverlayClick);
    }

    _bindCartIcon() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.removeEventListener('click', this._onCartIconClick);
      cartLink.removeEventListener('keydown', this._onCartIconKeydown);

      if (isCartPage()) {
        cartLink.removeAttribute('role');
        cartLink.removeAttribute('aria-haspopup');
        cartLink.removeAttribute('aria-expanded');
        cartLink.removeAttribute('aria-controls');
        return;
      }

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');
      cartLink.setAttribute('aria-controls', 'CartDrawer');

      cartLink.addEventListener('click', this._onCartIconClick);
      cartLink.addEventListener('keydown', this._onCartIconKeydown);
    }

    _bindCloseButtons() {
      const btns = this.querySelectorAll('.drawer__close');
      if (!btns.length) return;
      btns.forEach((btn) => {
        btn.removeEventListener('click', this._onCloseClick);
        btn.addEventListener('click', this._onCloseClick);
      });
    }

    _panelEl() {
      return this.querySelector('.drawer__inner');
    }

    _focusTrapContainer() {
      return this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
    }

    _clearTimers() {
      if (this._closeTimer) clearTimeout(this._closeTimer);
      if (this._openTimer) clearTimeout(this._openTimer);
      this._closeTimer = null;
      this._openTimer = null;
    }

    _getMsVar(name, fallback) {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      if (!raw) return fallback;
      if (raw.endsWith('ms')) return parseFloat(raw) || fallback;
      if (raw.endsWith('s')) return parseFloat(raw) * 1000 || fallback;
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : fallback;
    }

    _forceInstantScrollOn() {
      this._prevInlineScrollBehavior = document.documentElement.style.scrollBehavior || '';
      document.documentElement.style.scrollBehavior = 'auto';
    }

    _forceInstantScrollOff() {
      document.documentElement.style.scrollBehavior = this._prevInlineScrollBehavior;
    }

    _lockScroll() {
      if (this._locked) return;
      this._locked = true;

      this._forceInstantScrollOn();

      const html = document.documentElement;

      this._prevHtmlOverflow = html.style.overflow || '';
      this._prevHtmlTouchAction = html.style.touchAction || '';
      this._prevHtmlOverscroll = html.style.overscrollBehavior || '';

      const cur = Number(html.dataset.drawerLockCount || 0) + 1;
      html.dataset.drawerLockCount = String(cur);
      html.classList.add('drawer-lock');

      if (document.body.classList.contains('overflow-hidden')) {
        document.body.classList.remove('overflow-hidden');
      }
    }

    _unlockScroll() {
      if (!this._locked) return;
      this._locked = false;

      const html = document.documentElement;
      const cur = Number(html.dataset.drawerLockCount || 0);
      const next = Math.max(0, cur - 1);
      html.dataset.drawerLockCount = String(next);
      if (next === 0) html.classList.remove('drawer-lock');

      html.style.overflow = this._prevHtmlOverflow;
      html.style.touchAction = this._prevHtmlTouchAction;
      html.style.overscrollBehavior = this._prevHtmlOverscroll;

      requestAnimationFrame(() => this._forceInstantScrollOff());
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    _onKeyUp(evt) {
      if ((evt.key || '').toLowerCase() === 'escape') this.close();
    }

    _onOverlayClick() {
      this.close();
    }

    _onCloseClick(e) {
      e.preventDefault();
      this.close();
    }

    _onCartIconClick(event) {
      if (isCartPage()) return;
      event.preventDefault();
      this.open(event.currentTarget);
    }

    _onCartIconKeydown(event) {
      if (isCartPage()) return;

      const key = (event.key || '').toLowerCase();
      const code = (event.code || '').toUpperCase();

      if (key === ' ' || key === 'enter' || code === 'SPACE' || code === 'ENTER') {
        event.preventDefault();
        this.open(event.currentTarget);
      }
    }

    open(triggeredBy) {
      if (isCartPage()) return;

      this._clearTimers();

      if (triggeredBy) this.setActiveElement(triggeredBy);

      this.classList.remove('is-closing');
      this._lockScroll();

      requestAnimationFrame(() => this.classList.add('active'));

      const cartLink = document.querySelector('#cart-icon-bubble');
      if (cartLink) cartLink.setAttribute('aria-expanded', 'true');

      const panel = this._panelEl();

      const onOpenDone = () => {
        const container = this._focusTrapContainer();
        const focusEl =
          this.querySelector('.drawer__close') ||
          getFocusables(container)[0] ||
          panel ||
          container;

        this._focusTrap.activate(this.activeElement, focusEl);
      };

      const openDur = Math.max(
        this._getMsVar('--cd-panel-open-dur', 800),
        this._getMsVar('--cd-overlay-dur', 360)
      );

      if (panel) {
        const handle = (e) => {
          if (e.target !== panel) return;
          panel.removeEventListener('transitionend', handle);
          onOpenDone();
        };
        panel.addEventListener('transitionend', handle);

        this._openTimer = setTimeout(() => {
          panel.removeEventListener('transitionend', handle);
          onOpenDone();
        }, openDur + 140);
      } else {
        onOpenDone();
      }
    }

    close() {
      this._clearTimers();

      if (!this.classList.contains('active') && !this.classList.contains('is-closing')) return;

      this.classList.add('is-closing');
      this.classList.remove('active');

      const cartLink = document.querySelector('#cart-icon-bubble');
      if (cartLink) cartLink.setAttribute('aria-expanded', 'false');

      this._focusTrap.deactivate();

      const panel = this._panelEl();

      const finishClose = () => {
        this.classList.remove('is-closing');
        this._unlockScroll();
      };

      const closeDur = this._getMsVar('--cd-panel-close-dur', 900);

      if (panel) {
        const handle = (e) => {
          if (e.target !== panel) return;
          panel.removeEventListener('transitionend', handle);
          finishClose();
        };
        panel.addEventListener('transitionend', handle);

        this._closeTimer = setTimeout(() => {
          panel.removeEventListener('transitionend', handle);
          finishClose();
        }, closeDur + 160);
      } else {
        finishClose();
      }
    }

    _swapFromSectionHTML(sectionHTML, selectorToExtract, targetEl) {
      if (!sectionHTML || !targetEl) return;
      const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const extracted = doc.querySelector(selectorToExtract);
      if (!extracted) return;
      targetEl.innerHTML = extracted.innerHTML;
    }

    renderContents(parsedState) {
      const isEmpty =
        (typeof parsedState?.item_count === 'number' && parsedState.item_count === 0) ||
        (Array.isArray(parsedState?.items) && parsedState.items.length === 0);

      this.classList.toggle('is-empty', !!isEmpty);

      const inner = this.querySelector('.drawer__inner');
      if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

      const drawerSectionHTML = parsedState?.sections?.['cart-drawer'];
      const cartDrawerRoot = document.querySelector('#CartDrawer');

      if (drawerSectionHTML && cartDrawerRoot) {
        this._swapFromSectionHTML(drawerSectionHTML, '#CartDrawer', cartDrawerRoot);
      }

      updateCartCountBubble(parsedState?.item_count || 0);

      this._bindOverlay();
      this._bindCartIcon();
      this._bindCloseButtons();

      if (isCartPage()) return;

      const fallbackTrigger = this.activeElement || document.querySelector('#cart-icon-bubble');
      this.open(fallbackTrigger);
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  if (window.CartItems && !customElements.get('cart-drawer-items')) {
    class CartDrawerItems extends window.CartItems {}
    customElements.define('cart-drawer-items', CartDrawerItems);
  }

  /* ===========================
     FREE GIFT
  =========================== */

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart.js failed');
    return res.json();
  }

  async function updateCart(payload) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('cart/update failed');
    return res.json();
  }

  async function refreshCartUI() {
  const cartPage = document.querySelector('cart-page');
  if (cartPage && typeof cartPage.onCartUpdate === 'function') {
    cartPage.onCartUpdate();
    return;
  }

  const drawerItems = document.querySelector('cart-drawer-items');
  if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
    drawerItems.onCartUpdate();
    return;
  }

  const cart = await getCart();
  updateCartCountBubble(cart?.item_count || 0);
}

  const GIFT_OPT_OUT_KEY = 'gift_opt_out';

  const setLocalOptOut = (val) => {
    try {
      sessionStorage.setItem(GIFT_OPT_OUT_KEY, val ? '1' : '0');
    } catch (_) {}
  };

  const getLocalOptOut = () => {
    try {
      return sessionStorage.getItem(GIFT_OPT_OUT_KEY) === '1';
    } catch (_) {
      return false;
    }
  };

  async function syncFreeGiftWithCart() {
    try {
      const cart = await getCart();

      const giftIdRaw = theme?.free_gift?.variant_id;
      const giftId = Number(giftIdRaw);
      if (!giftId) return false;

      const QUALIFYING_PRODUCT_IDS = [15048765735177, 15292120236297];
      const hasQualifying = (cart.items || []).some((i) => QUALIFYING_PRODUCT_IDS.includes(i.product_id));

      const hasGift = (cart.items || []).some((i) => Number(i.id) === giftId);
      const cartOptOut = cart.attributes?.gift_opt_out === '1';
      const localOptOut = getLocalOptOut();
      const optedOut = cartOptOut || localOptOut;

      if (!hasQualifying) {
        if (cartOptOut) await updateCart({ attributes: { ...(cart.attributes || {}), gift_opt_out: '0' } });
        if (localOptOut) setLocalOptOut(false);
      }

      const eligible = hasQualifying && !optedOut;

      if ((eligible && hasGift) || (!eligible && !hasGift)) return false;

      const updates = {};
      updates[giftId] = eligible ? 1 : 0;

      await updateCart({ updates, attributes: { ...(cart.attributes || {}), _source_type: 'free_gift' } });
      return true;
    } catch (e) {
      console.error('Free gift sync failed:', e);
      return false;
    }
  }

  function reconcileGiftAndRefresh() {
    syncFreeGiftWithCart().then((changed) => {
      if (changed) refreshCartUI().catch(console.warn);
      else getCart().then((c) => updateCartCountBubble(c?.item_count || 0)).catch(() => {});
    });
  }

  function deferReconcile(ms = 700) {
    clearTimeout(deferReconcile._t);
    deferReconcile._t = setTimeout(reconcileGiftAndRefresh, ms);
  }

  async function markGiftOptOut() {
    setLocalOptOut(true);
    try {
      const cart = await getCart();
      await updateCart({ attributes: { ...(cart.attributes || {}), gift_opt_out: '1' } });
    } catch (e) {
      console.error('Failed to set gift opt-out:', e);
    }
  }

  function wireRemoveListeners() {
    const giftIdRaw = theme?.free_gift?.variant_id;
    const giftId = Number(giftIdRaw);
    if (!giftId) return;

    const removeSelectors = [
      '#CartDrawer-Remove',
      '.cart-remove__button',
      'button[name="remove"]',
      '.cart-item__remove',
      '[data-action="remove"]',
      'a[href*="change?line"]',
      'button[data-cart-remove]',
      '[data-cart-remove]',
    ].join(',');

    const handleIntent = async (e) => {
      const trigger = e.target.closest(removeSelectors);
      if (!trigger) return;

      const row = trigger.closest('[data-variant-id], .cart-item, li, tr, .cart-items__row');
      const dataVarId = row?.getAttribute?.('data-variant-id') || row?.dataset?.variantId;

      const inputId =
        row?.querySelector?.('input[name="id"]')?.value ||
        trigger?.getAttribute?.('value') ||
        null;

      const varIdSeen = Number(dataVarId || inputId || 0);

      if (varIdSeen && varIdSeen === giftId) {
        await markGiftOptOut();
        deferReconcile(800);
      } else {
        deferReconcile(800);
      }
    };

    document.body.addEventListener('pointerdown', handleIntent, { capture: true });
    document.body.addEventListener('click', handleIntent, { capture: true });

    document.body.addEventListener(
      'keydown',
      (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.closest(removeSelectors)) handleIntent(e);
      },
      { capture: true }
    );
  }

  document.addEventListener('DOMContentLoaded', () => {
    getCart().then((c) => updateCartCountBubble(c?.item_count || 0)).catch(() => {});

    reconcileGiftAndRefresh();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => deferReconcile());
    } else {
      document.body.addEventListener('click', (e) => {
        const btn = e.target.closest('button[name="add"]');
        if (btn) deferReconcile();
      });

      document.body.addEventListener('change', (e) => {
        if (
          e.target.closest('.cart-drawer') ||
          e.target.closest('cart-drawer') ||
          e.target.closest('form[action="/cart"]')
        ) {
          deferReconcile();
        }
      });
    }

    wireRemoveListeners();
  });
})();